/**
 * Architecture générale :
 *
 *  GridColDef : Définition des colonnes d'un grid. Objets de configuration
 *    à déclarer par exemple dans une liste d'extPoints.
 *
 *  ICellBuilder : Constructeur du contenu des cellules d'une colonne et de manipulation
 *    des données issues de GridDataRow : tri, export...
 *    Les CellBuilderXxx sont instanciés pour chaque grid via ColumnDef.getCellBuilderFactory(gridCol).call(gridCol).
 *    Api :
 *      redrawCell(gridDataRow, rootNode) : dessine le contenu d'une cellule.
 *
 *  IGridDataHolder : Fournisseur des données via des IGridDataRow pour remplir une grille.
 *      connectToGrid() : Connecte ce fournisseur de données à un et un seul widget IGrid
 *      grid : IGrid
 *      countRows() : int
 *      getRow(offset) : IGridDataRow
 *
 *  IGridDataRow : Représente une ligne de la grille et contient les données associées
 *      getData(key) : Donnée de clé key pour les CellBuilder.
 *
 *  GridCol : Colonne instanciée d'un IGrid gérant son éta courant :
 *      grid : IGrid
 *      colDef : GridColDef
 *      cellBuider : ICellBuilder
 *      hidden: boolean
 *
 *  IGrid (c-grid) : CustomElement racine du grid et coordonnant tous ces objets :
 *      dataHolder : IGridDataHolder
 *      columns : GridCol[]
 *
 *
 * (Re)Dessinement de la grid (quand les données sont chargées / modifiées) :
 *
 *  scGrid.invalidateRows(offset?:number, count?:number) : modification des rows existantes
 *  scGrid.rowCountChanged(offset:number, count:number) : insertion / suppression de rows
 *
 *
 * ActiveRow et sélection
 *
 * Le widget grid gère une et une seule row "active" (celle qui détient le focus), et un mécanisme de sélection
 * qui peut-être mono ou multi. En mode mono, la row active correspond à la row sélectionnée.
 * A chaque changement de sélection, un event "grid-select" est dispatché.
 *
 * Tri :
 *  IGrid (ie c-grid) :
 *      isSortKeyAlterable() : les fonctions de tri dynamiques sont-elles actives ?
 *      getMainSortCol() : colonne de la clé de tri principale ou null.
 *      getMainSortColState() : ordre de tri de la colonne de la clé de tri principale ("ascendant" ou "descendant").
 *  ICellBuilder :
 *      getColSortFn() : retourne une function de tri **ascendant** associé à cette colonne
 *  IGridDataHolderSortable :
 *      onSortFnChange() : appelé par c-grid lorsque les règles de tri ont changées. Si fct indefinie, la fonction de tr est désactivée.
 *      getNaturalSortFn() : retourne une function de tri redonnant l'ordre naturel des données.
 *  GridCol :
 *      setAsMainSortCol(state: ESortState, preserveSortForOtherCols: boolean)
 *      getColDirSortFn()
 *      sortPriority
 */

import {BaseElement} from "back/commons/basis";
import {REG} from 'lib/commons/registry';
import {DOM, JSX} from "lib/commons/xml/dom";

/** Api d'un widget de type grid. */
export interface IGrid extends BaseElement {

	/** Liste de définition des colonnes utilisées par ce grid. */
	columnDefs: GridColDef[]

	/** Colonnes instanciées selon les columnDefs pour ce grid.*/
	readonly columns: GridCol[]

	/**
	 * Surcharge du (re)dessinement des lines.
	 * Note : une line est la représentation HTML d'une row (issue du dataHolder).
	 * A un instant T, toutes les rows n'ont pas nécessairement de line associée.
	 */
	lineDrawer?: IGridLineDrawer

	/** Fournisseur de données connecté à ce grid. */
	dataHolder: IGridDataHolder

	/**
	 * Fonction finale de tri (combinant les ordres ascendant et descendant et les colonnes de tri).
	 * Utilisée par IGridDataHolder pour trier ses données.
	 * Retourne null si pas d'ordre explicite.
	 * Cette clé de tri est en général gérée et modifiée via les GridCol et les widgets dans la ligne d'entête.
	 * Si cette clé de tri est modifié directement, les entêtes de colonnes ne seront pas mises à jour.
	 */
	sortFn: IRowSortFn


	/** Racine des entêtes de colonnes. */
	readonly headersNode: HTMLElement | null

	/** Container gérant le scroll. */
	readonly scrollNode: HTMLElement

	/**
	 * Contenu scrollé. Fils de this.scrollNode, contient this.linesNode,
	 * et selon l'implémentation contient this.headersNode ou pas.
	 */
	readonly bodyNode: HTMLElement

	/** Racine des lignes. A utiliser pour ajouter @draggable="true" et gérer les events de drag&drop. */
	readonly linesNode: HTMLElement

	/**
	 * Type de sélection.
	 * - mono: select simple sur l'event pointerdown
	 * - monoOver: select simple sur l'event pointerover
	 * - monoClick: select simple sur l'event click
	 * - multi: selection multiple.
	 */
	selType: 'mono' | 'monoOver' | 'monoClick' | 'multi' | 'none'

	/** Rafraîchit le grid suite à une modification des rows existantes. */
	invalidateRows(offset?: number, count?: number): void

	/** Rafraîchit le grid suite à une insertion / suppression de rows. */
	rowCountChanged(offset: number, count: number): void

	/** Rafraîchit le grid combinant une invalidation de rows et un changement de nb de lignes à un point donné. */
	spliceRows(start: number, deleteCount: number, insertCount: number): void

	/** Contenu à afficher lorsque le grid est vide. */
	emptyBody: Node | (() => Node)

	/** Refraichit la zone d'info lorsque le grid est vide. */
	refreshEmptyBody(): void

	/** Offset de ligne activée détenant le focus. -1 si aucune ligne active. */
	getActiveRow(): number

	/** Change la ligne détenant le focus. */
	setActiveRow(offset: number): void

	/** */
	isRowSelected(offset: number): boolean

	/** Offset de la 1ère ligne sélectionnée ou undefined (pratique si selType="mono").*/
	getSelectedRow(): number | undefined

	/** Nombre de rows sélectionnées. */
	countSelectedRows(): number

	/**
	 * retourne un tableau d'offsets sélectionnés : [0, 5] = 2 lignes sélectionnées : 0 et 5
	 * Si nombre négatif, correspond à une série continue à partir de l'offset précédent dans le tableau:
	 * - [0, 3, -5] = 4 lignes sélectionnées: 0, 3, 4 et 5
	 * - [0, -10000] = lignes sélectionnées de 0 à 10000 inclus.
	 */
	getSelectedRows(): number[]

	/**
	 * Simple offset ou tableau d'offset tel que définit dans getSelectedRows().
	 * -1 pour effacer la sélection.
	 */
	setSelectedRows(selEntries: number | number[]): void

	/** Ajoute des rows dans la sélection. */
	addSelectedRows(startOffset: number, endOffset: number): void

	/** Elimine des rows de la sélection. */
	removeSelectedRows(startOffset: number, endOffset: number): void

	/** Sélectionne / déselectionne une row. */
	toggleSelectedRow(offset: number): void

	/** Efface toute sélection et active row.*/
	clearSel(): void

	/** Zone de la sélection visible. */
	getSelRect(): DOMRect

	/** Evalue si les données peuvent être triées (vérifie aussi que IGridDataHolder.onSortFnChange existe). */
	isSortKeyAlterable(): boolean

	/** Scroll pour s'assurer que la ligne offset soit visible (ca pourrait ne pas etre l'activeRow). */
	ensureRowVisible(offset: number, scrollOpts?: ScrollIntoViewOptions): void

	/**
	 * Retourne l'offset de la 1ère ligne visible (permet de rétablir le scroll après un refetch complet).
	 * @return -1 si aucune ligne visible (grid vide, widget masqué...)
	 */
	getVisibleOffsetStart(): number

	/**
	 * Gestionnaire de drop sur les rows.
	 * Les autres paramètres permettent de surcharger les handlers (voir tree.ts)
	 */
	setRowDropMgr(rowDropMgr: IGridRowDropMgr,
								dragover?: (this: HTMLElement, ev: DragEvent) => void,
								onDropMarkChange?: (this: IGrid) => void,
								drop?: (this: HTMLElement, ev: DragEvent) => void): this
}

//const IS_Grid = function(n: any): n is IGrid {return n && ('columnDefs' in n)} as INodeFilter<IGrid>;

/**
 * Détenteur des données à afficher par un IGrid.
 * ATTENTION : un IGridDataHolder doit être affecté à un et UN SEUL IGrid.
 *
 * Note : interface minimaliste uniquement destinée aux besoins internes de IGrid. Chaque
 * implémentation propose une API indépendante d'accès en lecture/écriture à ses données
 * et devra dispatcher les changements au grid connecté.
 */
export interface IGridDataHolder {

	/** Grid auquel ce IGridDataHolder est rattaché. */
	readonly grid: IGrid

	/**
	 * Appelé par le widget IGrid pour activer ou désactiver la connection.
	 * Le widget doit alors être dans un état vierge (0 lignes, pas de sel...).
	 * Si le IGridDataHolder implémente IGridDataHolderSortable, il doit prendre
	 * en compte la clé de tri du widget via IGrid.getSortFn() avant de rafrichir
	 * l'affichage du widget via grid.rowCountChanged(0, this.countRows());
	 * Le widget IGrid appelle cette méthode avec null en paramètre pour déconnecter si
	 * un nouveau dataHolder est associé au widget.
	 */
	connectToGrid(grid: IGrid | null): void

	/** Nombre de ligne totales à afficher par le IGrid. */
	countRows(): number

	/** Retourne une ligne pour le IGrid. null si hors bornes. */
	getRow(offset: number): IGridDataRow

	/** Retourne la rowKey pour un offset donné. null si hors bornes. */
	getRowKey(offset: number): any

	/**
	 * Retourne l'offset d'affichage dans le grid pour cette IGridDataRow.rowKey.
	 * Retourne -1 si cette rowKey n'est pas affichée dans le grid.
	 */
	getOffset(rowKey: any): number

}


/** IGridDataHolder en capacité de trier les données. */
export interface IGridDataHolderSortable extends IGridDataHolder {

	/**
	 * Doit être par le widget IGrid lorsque les règles de tri ont changé.
	 * L'implémentation doit utiliser IGrid.getSortFn() pour trier les données.
	 */
	onSortFnChange(): void

	/**
	 * Retourne une fonction de tri permetant de réobtenir les données dans l'ordre naturel original.
	 * Retourne null si fonction non disponible.
	 */
	getNaturalSortFn(): IRowSortFn | null
}

export function isGridDataHolderSortable(g: IGridDataHolder): g is IGridDataHolderSortable {return g && ('onSortFnChange' in g)}


/** Représente une ligne de données du grid fournie par le IGridDataHolder. */
export interface IGridDataRow {

	/** Donnée de clé key pour les ICellBuilder.*/
	getData<T extends string>(key: T): any

	/**
	 * Identifiant opaque de la row susceptible d'être mémorisé par les widgets des cellules construites par les ICellBuilder
	 * pour implémenter des interactions avec le dataHolder :
	 * - open/close de dossiers (cf tree),
	 * - drag and drop de lignes,
	 * - etc.
	 */
	readonly rowKey: any

	/**
	 * Objet sur lequel les ICellBuilder peuvent associer des données calculées en cache pour cette row (et uniquement cette row).
	 * Pour éviter tout conflit, il est préférable d'utiliser des Symbol pour référencer les données mises en cache.
	 * L'implémentation de IGridDataRow se charge de reseter ce cache en cas de modification de la row.
	 * Pour le ICellBuilder, si la donnée en cache est présente, elle est donc nécessairement à jour.
	 */
	readonly cacheHolder: any
}


/**
 * Définition d'une colonne d'un grid.
 */
export class GridColDef {

	protected _label: string | dynString;
	protected _description: string | dynString;
	protected _flexBasis: string | dynString;
	protected _flexGrow: number | dynNumber;
	protected _flexShrink: number | dynNumber;
	protected _minWidth: string | dynString;
	protected _maxWidth: string | dynString;
	protected _skin: string | dynString;
	protected _hidden: boolean | dynBoolean;
	protected _sortable: boolean | dynBoolean;
	protected _cellBuilder: ICellBuilder | ((col: GridCol) => ICellBuilder);
	protected _defaultSortPriority: number;
	protected _defaultSortDir: ESortDir;

	constructor(public id: string) {}

	/**
	 * Une colonne non disponible n'est ni visible ni accessible via le columnPicker.
	 * Méthode appelée avant la construction de l'objet GridCol.
	 * Peut être surchargé pour ne créer cette colonne que sous certaines conditions.
	 */
	isAvailable(grid: IGrid): boolean {return true}

	getLabel(col: GridCol): string {return (typeof this._label === 'function') ? this._label(col) : (this._label !== undefined) ? this._label : this.id}

	setLabel(label: string | dynString): this {
		this._label = label;
		return this;
	}

	getDescription(col: GridCol): string {return (typeof this._description === 'function') ? this._description(col) : this._description}

	setDescription(description: string | dynString): this {
		this._description = description;
		return this;
	}

	getFlexBasis(col: GridCol): string {return (typeof this._flexBasis === 'function') ? this._flexBasis(col) : this._flexBasis || "4em"}

	setFlexBasis(val: string | dynString): this {
		this._flexBasis = val;
		return this;
	}

	getFlexGrow(col: GridCol): number {return (typeof this._flexGrow === 'function') ? this._flexGrow(col) : this._flexGrow || 0}

	setFlexGrow(val: number | dynNumber): this {
		this._flexGrow = val;
		return this;
	}

	getFlexShrink(col: GridCol): number {return (typeof this._flexShrink === 'function') ? Math.max(this._flexShrink(col), 0) : this._flexShrink || 0}

	setFlexShrink(val: number | dynNumber): this {
		//XXX Retrouver pourquoi ? Pratique pour des petites colonnes à largeur fixe de flags. Pb si largeur non maitrisée ?
		// Si vraiment nécessaire, jouer avec MinWidth.
		// if (val === 0) {
		// 	console.warn("Set flex shrink to 0 for grid column not allowed. Forced to .1", this);
		// 	val = .1;
		// }
		this._flexShrink = val;
		return this;
	}

	setFlex(basis: string | dynString, grow: number | dynNumber = 1, shrink: number | dynNumber = 1): this {
		this.setFlexBasis(basis);
		this._flexGrow = grow;
		this.setFlexShrink(shrink);
		return this;
	}

	getMinWidth(col: GridCol): string {return (typeof this._minWidth === 'function') ? this._minWidth(col) : this._minWidth || "0"}

	setMinWidth(val: string | dynString): this {
		this._minWidth = val;
		return this;
	}

	getMaxWidth(col: GridCol): string {return (typeof this._maxWidth === 'function') ? this._maxWidth(col) : this._maxWidth || ""}

	setMaxWidth(val: string | dynString): this {
		this._maxWidth = val;
		return this;
	}

	getSkin(col: GridCol): string {return (typeof this._skin === 'function') ? this._skin(col) : this._skin || ""}

	setSkin(val: string | dynString): this {
		this._skin = val;
		return this;
	}


	/**
	 * Une colonne sera réellement triable :
	 * - si cette méthode retourne true (true par défaut)
	 * - si ICellBuilder.getSortFn() est non null.
	 * - et si IGrid.isSortKeyAlterable() (qui interroge les capacités du IGridDataHolder)
	 */
	isSortable(col: GridCol): boolean {
		return (typeof this._sortable === 'function') ? this._sortable(col) : this._sortable !== false;
	}

	setSortable(val: boolean | dynBoolean): this {
		this._sortable = val;
		return this;
	}

	getDefaultSortPriority(col: GridCol): number {return this._defaultSortPriority || 0}

	getDefaultSortDir(col: GridCol): ESortDir | null {return this._defaultSortDir}

	setDefaultSort(priority: number, dir: ESortDir): this {
		this._defaultSortPriority = priority;
		this._defaultSortDir = dir;
		return this;
	}

	/**
	 * Une colonne peut-être masquée mais affichable via le columnPicker.
	 */
	isHidden(col: GridCol): boolean {
		return (typeof this._hidden === 'function') ? this._hidden(col) : this._hidden === true;
	}

	setHidden(val: boolean | dynBoolean): this {
		this._hidden = val;
		return this;
	}

	getCellBuilder(col: GridCol): ICellBuilder {
		return (typeof this._cellBuilder === 'function') ? this._cellBuilder(col) : this._cellBuilder;
	}

	setCellBuilder(cellBuilder: ICellBuilder | ((col: GridCol) => ICellBuilder)): this {
		this._cellBuilder = cellBuilder;
		return this;
	}

	/** Instancie une colonne issue de cette définition et pour ce grid. */
	newCol(grid: IGrid): GridCol {return new GridCol(grid, this)}

	/** Calcul les propriétés génériques de GridCol, ajoute éventuellement des skins, des listeners... */
	initCol(col: GridCol) {
		col.hidden = this.isHidden(col);
		col.cellBuilder = this.getCellBuilder(col); //déclaré même si colonne cachée : peut servir aux tris ou à des calculs intermémdiaires.
		if (col.hidden) return;
		col.flex = this.getFlexGrow(col) + " " + this.getFlexShrink(col) + " " + this.getFlexBasis(col);
		col.minWidth = this.getMinWidth(col);
		col.maxWidth = this.getMaxWidth(col);
		const skin = this.getSkin(col);
		if (skin) col.installedSkin = REG.findReg(col.grid).installSkin(skin, col.grid.shadowRoot);
		if (this._defaultSortPriority) col.sortPriority = this.getDefaultSortPriority(col);
		if (this._defaultSortDir) col.sortDir = this.getDefaultSortDir(col);
	}

	/** Suppression d'une colonne du grid. */
	onRemovedCol(col: GridCol) {
		if (col.installedSkin) col.installedSkin.remove();
	}

	/** Construit l'entête de la colonne à injecter dans rootNode. */
	buildColHeader(col: GridCol, root: HTMLElement) {
		col.rootHeader = root;
		if (this.isSortable(col) && col.cellBuilder.getColSortFn() != null && col.grid.isSortKeyAlterable()) {
			root.appendChild(
				<c-button class="colBody" label={this.getLabel(col)} title={this.getDescription(col)} gridsort="none" ui-context="custom" onclick={(ev: MouseEvent) => {
					if (col.sortPriority === 1) {
						//on click sur la colonne qui est déjà la colonne de tri: on permute l'ordre.
						if (col.sortDir === 'ascendant') {
							col.setAsMainSortCol('descendant', true);
						} else {
							const dataHolder = col.grid.dataHolder;
							if (isGridDataHolderSortable(dataHolder) && dataHolder.getNaturalSortFn() != null) {
								//Possibilité de revenir à l'état naturel, on y retourne.
								col.setAsMainSortCol('none', false);
							} else {
								col.setAsMainSortCol('ascendant', true);
							}
						}
					} else {
						//on change de colonne de tri prinicpale.
						col.setAsMainSortCol('ascendant', true);
					}
					ev.stopPropagation();
				}}/>);
		} else {
			root.appendChild(<div class="colBody" title={this.getDescription(col)}>{this.getLabel(col)}</div>);
		}
	}

	/**
	 * Appelé par le widget IGrid pour le refresh graphique des headers en fonction des ordres de tri.
	 * @param priority Egal à 1 pour la clé de tri principale, 2 pour la secondaire, etc.
	 *    Actuellement pas de représentation graphique pour les clés de tri secondaires.
	 */
	redrawColSortState(col: GridCol) {
		if (!col.rootHeader) return;
		const btn = col.rootHeader.querySelector("c-button");
		if (btn) DOM.setAttr(btn, 'gridsort', col.sortPriority === 1 ? col.sortDir : 'none' as ESortState);
	}

}

/** Propriétés dynamiques de GridColDef. */
type dynString = (col: GridCol) => string;
type dynNumber = (col: GridCol) => number;
type dynBoolean = (col: GridCol) => boolean;

/** Direction d'une clé de tri. */
export type ESortDir = 'ascendant' | 'descendant';
/** Statut courant du tri d'une colonne. */
export type ESortState = ESortDir | 'none';

/** Fonction de tri */
export type IRowSortFn = (r1: IGridDataRow, r2: IGridDataRow) => number;


/** Colonne d'un IGrid, issu d'un GridColDef. */
export class GridCol {

	/** Constructeur de l'intérieur de la cellule. */
	cellBuilder: ICellBuilder;

	/** Propiétés graphiques pour la construction des cellules de la colonne. */
	hidden: boolean;
	flex: string;
	minWidth: string;
	maxWidth: string;

	sortDir: ESortDir = 'ascendant';

	/** 0 : n'est pas une clé de tri, 1: clé de ti principale, 2: clé de tri secondaire...*/
	sortPriority: number = 0;

	/** Noeud HTML du header de la colonne. */
	rootHeader?: HTMLElement;

	/** Skin éventuellement installé par GridColDef. */
	installedSkin?: HTMLStyleElement;

	constructor(public grid: IGrid, public colDef: GridColDef) {}

	/** Active ou désactive cette colonne de tri. */
	setAsMainSortCol(state: ESortState, preserveSortForOtherCols: boolean) {
		const maxSortCriterions = 3; // A rendre configurable ?
		if (!this.grid.isSortKeyAlterable()) return;
		const newSortsFn: IRowSortFn[] = [];
		//On modifie les autres colonnes
		this.grid.columns.forEach((col) => {
			if (col === this) return;
			if (col.sortPriority > 0) {
				//cette col était une clé de tri
				if (preserveSortForOtherCols) {
					if (state === 'none') {
						if (this.sortPriority > 0 && this.sortPriority < col.sortPriority) col.sortPriority--;
					} else {
						if (this.sortPriority == 0 || this.sortPriority > col.sortPriority) col.sortPriority = col.sortPriority < maxSortCriterions ? col.sortPriority + 1 : 0;
					}
				} else {
					col.sortPriority = 0;
				}
				col.colDef.redrawColSortState(col);
				if (col.sortPriority > 0) newSortsFn[col.sortPriority - 1] = col.getColDirSortFn();
			}
		});
		//On traitre notre colonne
		if (state !== 'none') {
			this.sortDir = state;
			this.sortPriority = 1;
			newSortsFn[0] = this.getColDirSortFn();
		} else {
			this.sortPriority = 0;
		}
		this.colDef.redrawColSortState(this);
		if (newSortsFn.length > 0 && newSortsFn.length < maxSortCriterions && isGridDataHolderSortable(this.grid.dataHolder)) {
			//Si moins de maxSortKeys critères, on ajoute la clé de tri naturel.
			const naturalSort = this.grid.dataHolder.getNaturalSortFn();
			if (naturalSort) newSortsFn.push(naturalSort);
		}
		//on ordonne le changement de tri
		this.grid.sortFn = GridCol.buildSortFunction(newSortsFn);
	}

	getColDirSortFn(): IRowSortFn {
		const sortFn = this.cellBuilder.getColSortFn(); //jamais null ici (controlé en amont)
		return this.sortDir === 'descendant' ? function (r1: IGridDataRow, r2: IGridDataRow) {return -sortFn(r1, r2)} : sortFn;
	}

	static buildSortFunction(sortFns: IRowSortFn[]): IRowSortFn {
		if (sortFns.length == 0) {
			return null;
		} else if (sortFns.length == 1) {
			return sortFns[0];
		} else if (sortFns.length == 2) {
			const f1 = sortFns[0];
			const f2 = sortFns[1];
			return function (r1: IGridDataRow, r2: IGridDataRow): number {return f1(r1, r2) || f2(r1, r2)};
		} else {
			return function (r1: IGridDataRow, r2: IGridDataRow): number {
				for (let i = 0; i < sortFns.length; i++) {
					const result = sortFns[i](r1, r2);
					if (result != 0) return result;
				}
				return 0;
			}
		}
	}
}

/**
 * Constructeur de cellules.
 */
export interface ICellBuilder {

	/**
	 * Redessine la cellule dans le container root.
	 *
	 * Optimisation des refresh : L'implémentation des widgets IGrid garanti que
	 * root ne peut jamais avoir été utilisé par un autre ICellBuilder,
	 * mais peut avoir déjà été utilisé pour dessiner une autre row.
	 * Le CellBuilder peut donc construire une sous-structure stable et la
	 * rafraichir avec les nouvelles données issues de 'row'.
	 */
	redrawCell(row: IGridDataRow, root: HTMLElement): void

	/** Retourne une function de tri **ascendant** associé à cette colonne. */
	getColSortFn(): IRowSortFn | null
}

/**
 * Surcharge les règles d'affichage des lignes une fois les ICellBuilder.redrawCell() de chacune de ses cellules appelées.
 */
export interface IGridLineDrawer {
	redrawLine(row: IGridDataRow, line: HTMLElement): void
}

/**
 * Gestionnaire de drop associé à un IGrid.
 */
export interface IGridRowDropMgr {
	/**
	 * Pendant le drag, demande les possibilités de drop. L'implémentation de IGrid se charge du rendu
	 * graphique des possibiltés de drop en fonction du retour de cette méthode et de la position exacte de la souris.
	 *
	 * Si les drag est dans la zone "blanche" row et line sont null.
	 * Les positions retournées peuvent alors être :
	 * - EGridDropPos.after correspondant à une insertion après le dernier fils du root du grid.
	 * - EGridDropPos.over correspondant au over du root du grid.
	 */
	onDragOverRow(ev: DragEvent, grid: IGrid, row: IGridDataRow, line: HTMLElement): EGridDropPos

	/** Appelé au drop avec la position finale sélectionnée par l'utilisateur. */
	dropOnRow(ev: DragEvent, grid: IGrid, row: IGridDataRow, line: HTMLElement, pos: EGridDropPos): void
}

/** Position posible de drop autour d'une row du IGrid. */
export const enum EGridDropPos {
	none = 0, before = 1, over = 2, after = 4
}
